import React, { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface Props {
  children: ReactElement;
}

/** Gate authenticated pages. Two redirects:
 *
 *  1. Not logged in → /login
 *  2. Logged in but mustChangePassword=true → /change-password
 *     (unless we're already on that route — otherwise infinite loop)
 *
 *  Public auth pages render their own redirect-if-logged-in logic. */
export const ProtectedRoute: React.FC<Props> = ({ children }) => {
  const { isAuthenticated, isLoading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <FullScreenSpinner />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return children;
};

const FullScreenSpinner: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--text-muted)',
    }}
  >
    Loading…
  </div>
);
